import React from 'react';
import { Link } from 'react-router-dom';

function About() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About AdverseVideo Detector AI</h1>
          <p className="text-lg md:text-xl text-blue-100">
            Research-backed detection of cyber-adversarial content in social media video ecosystems.
          </p>
        </div>
      </div>

      <main className="container mx-auto px-4 py-12 max-w-5xl space-y-10">
        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
          <p className="text-gray-700 leading-relaxed">
            AdverseVideo Detector AI is a research-backed platform designed to identify cyber-adversarial
            content in social media videos. Built on peer-reviewed research published at the IEEE International
            Conference on Intelligence and Security Informatics (ISI).
          </p>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">The Problem</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Cyber threat actors increasingly use video platforms (YouTube, TikTok) to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Distribute hacking tutorials</li>
            <li>Demonstrate malware deployment</li>
            <li>Recruit for malicious activities</li>
            <li>Share zero-day exploits</li>
            <li>Coordinate cyber attacks</li>
          </ul>
          <p className="text-gray-700 mt-4 leading-relaxed">
            Traditional threat intelligence tools focus on text-based forums and paste sites, leaving
            video-based threats largely undetected.
          </p>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Our Solution</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            AdverseVideo Detector AI uses multimodal AI analysis to detect threat indicators across:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li><strong>Visual Analysis:</strong> GPT-4 Vision identifies hacking tools, terminal interfaces, and exploit demonstrations</li>
            <li><strong>Audio Analysis:</strong> Whisper AI transcribes and analyzes spoken content for threat keywords</li>
            <li><strong>Metadata Analysis:</strong> Pattern recognition across titles, descriptions, tags, and engagement metrics</li>
          </ul>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Research Foundation</h2>
          <ul className="space-y-2 text-gray-700">
            <li><strong>Paper:</strong> "Detecting Cyber-Adversarial Videos in Traditional Social Media"</li>
            <li><strong>Conference:</strong> IEEE International Conference on Intelligence and Security Informatics</li>
            <li><strong>Author:</strong> Bingyan Du, MS Business Analytics, Arizona State University</li>
            <li>
              <strong>Citation:</strong>{' '}
              <a
                href="https://scholar.google.com/citations?view_op=view_citation&hl=en&user=8QuUW00AAAAJ&citation_for_view=8QuUW00AAAAJ:u-x6o8ySG0sC"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                View IEEE paper citation
              </a>
            </li>
          </ul>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Who We Serve</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Academic researchers studying cyber threat dissemination</li>
            <li>Educational institutions protecting students from harmful content</li>
            <li>Cybersecurity professionals conducting threat analysis</li>
            <li>Small organizations monitoring security awareness training materials</li>
          </ul>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Technology Stack</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>GPT-4 Vision API (OpenAI)</li>
            <li>Whisper Speech-to-Text (OpenAI)</li>
            <li>YouTube Data API v3</li>
            <li>Custom risk scoring algorithms</li>
            <li>React frontend, Python/Flask backend</li>
          </ul>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Get Started</h2>
          <ul className="space-y-2 text-gray-700">
            <li><strong>Web App:</strong> adversevideo.org</li>
            <li>
              <strong>iOS App:</strong>{' '}
                <a
                  href="https://apps.apple.com/us/app/adversevideo-detector-ai/id6759844699"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                AdverseVideo Detector AI on the App Store
              </a>
            </li>
            <li><strong>Android App:</strong> [Google Play Link - Coming Soon]</li>
          </ul>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Contact</h2>
          <p className="text-gray-700 mb-2">For research collaborations, institutional partnerships, or feedback:</p>
          <ul className="space-y-2 text-gray-700">
            <li><strong>Email:</strong> contact@adversevideo.org</li>
            <li>
              <strong>LinkedIn:</strong>{' '}
              <a
                href="https://www.linkedin.com/in/bingyan-du/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                bingyan-du
              </a>
            </li>
          </ul>
        </section>

        <section className="text-center bg-blue-50 border border-blue-200 rounded-xl p-8">
          <p className="text-gray-700 italic">
            AdverseVideo Detector AI is a non-profit research initiative committed to advancing cybersecurity
            through AI-powered threat detection.
          </p>
          <div className="mt-6 space-x-3">
            <Link
              to="/"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              Back to Home
            </Link>
            <Link
              to="/how-it-works"
              className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
            >
              How It Works
            </Link>
            <Link
              to="/faq"
              className="inline-block bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition"
            >
              FAQ
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default About;
